import { sourceHttpClient } from '../core/SourceHttp.ts';
import { yomiriDatabase } from '../storage/YomiriDatabase.ts';

export type MediaKind = 'MANGA' | 'ANIME';
export interface HomeMedia {
  id: number;
  title: { romaji: string; english?: string | null; native?: string | null };
  synonyms?: string[];
  coverImage: { large: string; extraLarge?: string; color?:string|null };
  bannerImage?: string | null;
  description?: string | null;
  genres?:string[];
  format?:string|null;
  status?: string | null;
  episodes?: number | null;
  chapters?: number | null;
  duration?: number | null;
  season?: string | null;
  seasonYear?: number | null;
  nextAiringEpisode?: { episode: number; airingAt: number } | null;
  averageScore?: number | null;
  startDate?: { year?: number | null };
  type: MediaKind;
  isAdult?: boolean;
}
export interface HomeRecommendations { trending: HomeMedia[]; popular: HomeMedia[]; topRated?: HomeMedia[] }
interface RecommendationMedia extends HomeMedia { recommendations?: { nodes?: { mediaRecommendation?: HomeMedia | null }[] } }
interface QueryData { Media?: HomeMedia; trending?: { media?: HomeMedia[] }; popular?: { media?: HomeMedia[] }; topRated?: { media?: HomeMedia[] }; Page?: { media?: RecommendationMedia[] } }
interface Cached<T> { until: number; value: T }
const pending = new Map<string, Promise<unknown>>();
let cooldown = 0;
const fields = 'id type isAdult title{romaji english native} synonyms coverImage{large extraLarge color} bannerImage description(asHtml:false) genres format status episodes chapters duration season seasonYear nextAiringEpisode{episode airingAt} averageScore startDate{year}';
export const mediaTitle = (item: HomeMedia) => item.title.english || item.title.romaji || item.title.native || 'Sin título';
export interface DiscoveryFilters {search:string;format:string;status:string;sort:string;year:string;season:string;score:string;genres:string[]}
export function discoverAnime(filters:DiscoveryFilters,page:number):Promise<HomeMedia[]> {
 return discoverMedia('ANIME',filters,page);
}
export function discoverMedia(type:MediaKind,filters:DiscoveryFilters,page:number):Promise<HomeMedia[]> {
 const variables={page,search:filters.search||undefined,format:filters.format||undefined,status:filters.status||undefined,sort:filters.sort||'POPULARITY_DESC',year:filters.year?Number(filters.year):undefined,season:filters.season||undefined,score:filters.score?Number(filters.score)-1:undefined,genres:filters.genres.length?filters.genres:undefined};
 const yearFilter=type==='MANGA'?'startDate_like:$date':'seasonYear:$year';
 const allVariables={...variables,type,date:filters.year?filters.year+'%':undefined};
 const yearDeclaration=type==='MANGA'?'$date:String':'$year:Int';
 return cachedQuery('discover-v2:'+JSON.stringify(allVariables),`query($type:MediaType!,$page:Int!,$search:String,$format:MediaFormat,$status:MediaStatus,$sort:[MediaSort],${yearDeclaration}${type==='ANIME'?', $season:MediaSeason':''},$score:Int,$genres:[String]){Page(page:$page,perPage:36){media(type:$type,isAdult:false,search:$search,format:$format,status:$status,sort:$sort,${yearFilter}${type==='ANIME'?',season:$season':''},averageScore_greater:$score,genre_in:$genres){${fields}}}}`,allVariables,data=>(data.Page?.media||[]).filter(row=>!row.isAdult));
}
const normalize = (value: string) => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

async function cachedQuery<T>(key: string, query: string, variables: Record<string, unknown>, parse: (data: QueryData) => T): Promise<T> {
  const existing = pending.get(key);
  if (existing) return existing as Promise<T>;
  const job = (async () => {
    const cached = await yomiriDatabase.getSetting<Cached<T>>(key).catch(() => undefined);
    if (cached && cached.until > Date.now()) return cached.value;
    if (Date.now() < cooldown) {
      if (cached) return cached.value;
      throw new Error('Las sugerencias están temporalmente limitadas. Probá más tarde.');
    }
    try {
      const result = await sourceHttpClient.text({ sourceId: 'anilist', url: 'https://graphql.anilist.co', method: 'POST', corsOpen: true, credentials: 'omit', responseType: 'text', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ query, variables }), cache: { ttlMs: 0, scope: 'public' } });
      if (result.status === 429) { cooldown = Date.now() + 60000; throw new Error('AniList está temporalmente limitado.'); }
      const payload = JSON.parse(result.body) as { data?: QueryData; errors?: unknown[] };
      if (result.status >= 400 || payload.errors || !payload.data) throw new Error('No se pudieron actualizar las sugerencias.');
      const value = parse(payload.data);
      await yomiriDatabase.setSetting(key, { value, until: Date.now() + 6 * 3600000 }).catch(() => {});
      return value;
    } catch (cause) {
      if (cached) return cached.value;
      throw cause;
    }
  })().finally(() => pending.delete(key));
  pending.set(key, job);
  return job;
}
export async function readHomeCache(type: MediaKind): Promise<HomeRecommendations | undefined> {
  const cached = (await yomiriDatabase.getSetting<Cached<HomeRecommendations>>(`home-discovery-v5:${type}`).catch(() => undefined))?.value;
  if (cached) {
    cacheMediaItems([...(cached.trending || []), ...(cached.popular || []), ...(cached.topRated || [])]);
  }
  return cached;
}
export const memoryMediaCache = new Map<number, HomeMedia>();

export function cacheMediaItems(items: (HomeMedia | null | undefined)[]) {
  for (const item of items) {
    if (item && item.id) {
      memoryMediaCache.set(item.id, item);
    }
  }
}

export function getCachedDiscoveryMediaSync(id: number): HomeMedia | undefined {
  return memoryMediaCache.get(id);
}

export function getHomeRecommendations(type: MediaKind): Promise<HomeRecommendations> {
  return cachedQuery(`home-discovery-v5:${type}`, `query($type:MediaType!){trending:Page(perPage:20){media(type:$type,sort:TRENDING_DESC,isAdult:false){${fields}}}popular:Page(perPage:20){media(type:$type,sort:POPULARITY_DESC,isAdult:false){${fields}}}topRated:Page(perPage:20){media(type:$type,sort:SCORE_DESC,isAdult:false){${fields}}}}`, { type }, data => {
    const trending = (data.trending?.media || []).filter(row => !row.isAdult);
    const popular = (data.popular?.media || []).filter(row => !row.isAdult);
    const topRated = (data.topRated?.media || []).filter(row => !row.isAdult);
    cacheMediaItems([...trending, ...popular, ...topRated]);
    return { trending, popular, topRated };
  });
}
export function getDiscoveryMedia(id: number): Promise<HomeMedia> {
  const inMemory = memoryMediaCache.get(id);
  if (inMemory) return Promise.resolve(inMemory);
  if (!Number.isSafeInteger(id) || id <= 0) return Promise.reject(new Error('Identificador de obra inválido.'));
  return cachedQuery(`discovery-detail-v1:${id}`, `query($id:Int!){Media(id:$id){${fields}}}`, { id }, data => {
    if (!data.Media) throw new Error('No se encontró esta obra.');
    return data.Media;
  });
}
export function openDiscoveryDetails(item: HomeMedia) {
  if (item && item.id) memoryMediaCache.set(item.id, item);
  history.pushState({yomiriDetailFrom:location.pathname+location.search}, '', '/?' + new URLSearchParams({section:'detail',media:String(item.id)}));
  window.dispatchEvent(new PopStateEvent('popstate'));
}
export interface HomeRelated { media?: HomeMedia; related: HomeMedia[] }
export function getHomeRelated(title: string, type: MediaKind): Promise<HomeRelated> {
  return cachedQuery(`home-related-v3:${type}:${normalize(title)}`, `query($title:String!,$type:MediaType!){Page(perPage:5){media(search:$title,type:$type,isAdult:false){${fields} recommendations(sort:RATING_DESC,perPage:12){nodes{mediaRecommendation{${fields}}}}}}}`, { title, type }, data => {
    const exact = (data.Page?.media || []).find((row: HomeMedia) => [row.title.romaji, row.title.english, row.title.native, ...(row.synonyms || [])].some(name => name && normalize(name) === normalize(title)));
    return { media: exact, related: (exact?.recommendations?.nodes || []).map(node => node.mediaRecommendation).filter((row: HomeMedia | null | undefined): row is HomeMedia => !!row && row.type === type && !row.isAdult) };
  });
}

export async function getRelatedRecommendations(title: string, type: MediaKind): Promise<HomeMedia[]> {
  return (await getHomeRelated(title, type)).related;
}
